/**
 * Page intermédiaire "Find or Create" pour les tâches de progression.
 *
 * Si un document de l'extension demandée existe déjà → redirige vers /documents/:id.
 * Si aucun → en crée un automatiquement avec un nom par défaut → redirige.
 * Affiche un spinner pendant l'opération.
 */
import type { DocumentExtension } from '@app/contracts';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { documentViewSearch } from '../../../../router';
import { createDocument, fetchDocuments } from '../../api/index.js';

const EXTENSION_LABELS: Record<DocumentExtension, string> = {
  swot: 'Analyse SWOT',
  pestel: 'Analyse PESTEL',
  needsWait: 'Parties intéressées',
  paOperational: "Plan d'actions préventives",
  auditPlanning: "Plan d'audit",
  auditReport: "Rapport d'audit",
  nonConformity: 'Non-conformité',
  managementReview: 'Revue de direction',
};

interface Props {
  extension: DocumentExtension;
  /** Propage `openDocumentRights` vers la page `/documents/:id` après résolution du document. */
  forwardOpenDocumentRights?: boolean;
}

export function ContextDocumentPage({ extension, forwardOpenDocumentRights }: Props) {
  const navigate = useNavigate();
  const initiated = useRef(false);

  useEffect(() => {
    if (initiated.current) return;
    initiated.current = true;

    async function findOrCreate() {
      const docs = await fetchDocuments({ extension });
      const active = docs.filter((d) => d.active);

      if (active.length > 0 && active[0]) {
        void navigate({
          to: '/documents/$id',
          params: { id: active[0].id },
          search: forwardOpenDocumentRights
            ? documentViewSearch.openRights
            : documentViewSearch.closed,
        });
        return;
      }

      const created = await createDocument({
        name: `${EXTENSION_LABELS[extension]} — ${new Date().toLocaleDateString('fr-FR')}`,
        extension,
        strictValidation: false,
      });
      void navigate({
        to: '/documents/$id',
        params: { id: created.id },
        search: forwardOpenDocumentRights
          ? documentViewSearch.openRights
          : documentViewSearch.closed,
      });
    }

    findOrCreate().catch(() => {
      void navigate({ to: '/documents' });
    });
  }, [extension, navigate, forwardOpenDocumentRights]);

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24">
      <span className="relative flex h-12 w-12" aria-hidden="true">
        <span className="absolute inset-0 rounded-full border-[3px] border-slate-200" />
        <span className="absolute inset-0 animate-spin rounded-full border-[3px] border-transparent border-t-emerald-500" />
      </span>
      <p className="text-sm font-medium text-slate-500">
        Chargement de {EXTENSION_LABELS[extension].toLowerCase()}…
      </p>
    </div>
  );
}
